import {useMatches} from 'react-router-dom';

import {isNil, last} from 'ramda';

import {
  getPageModule,
  getPageRoute,
  getPageSection,
  pushGtmPageView,
  setClarityTag,
  useOnMount,
} from 'shared/web';

import {isStageAnalyticsEnabled} from '../../../utils/isStageAnalyticsEnabled';

interface PageViewPushProps {
  pageRoute: string;
  pageSection: string;
  pageModule: string;
}

function PageViewPush(props: PageViewPushProps) {
  useOnMount(function announcePageView() {
    pushGtmPageView(props.pageRoute, props.pageSection, {
      app: 'dms',
      module: props.pageModule,
      product: 'omnetic',
    });
    setClarityTag('module', props.pageModule);
  });

  return null;
}

/**
 * Sends one page view per screen the user opens, named after the route rather
 * than after the URL or the translated title.
 *
 * The route pattern comes from the match react-router made, which is the only
 * place that knows which segments were parameters — a regex over the URL would
 * have to guess.
 *
 * The key is the concrete path, not the route, so that moving between two
 * vehicles counts as two views of the vehicle screen. Remounting is what makes
 * `useOnMount` fire once per screen, instead of an effect on a changing
 * dependency.
 */
export function GtmPageView() {
  const leafMatch = last(useMatches());

  if (isNil(leafMatch)) {
    return null;
  }

  const pageRoute = getPageRoute(leafMatch.pathname, leafMatch.params);
  const isStage = isStageAnalyticsEnabled();
  // A catch-all route can normalize to `/{*}`. On Stage, classify its concrete
  // pathname so it does not appear as an unknown module in the product overview.
  const pageModule = getPageModule(isStage ? leafMatch.pathname : pageRoute);

  return (
    <PageViewPush
      key={leafMatch.pathname}
      pageRoute={pageRoute}
      pageSection={getPageSection(leafMatch.pathname)}
      pageModule={pageModule}
    />
  );
}
